// piano-frontend/src/components/PracticeConfigDialog.js
import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../App';
import { useDialog } from '../contexts/DialogProvider';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  Chip,
  Alert,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { Schedule, Delete, Add, EventNote, Warning, Edit, Check, Close } from '@mui/icons-material';
import api from '../api/axios';
import dayjs from 'dayjs';
import DateImpactPreview from './DateImpactPreview';
import DateField from './DateField';

// 30분 단위 시간 옵션 생성
const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      options.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const DAY_CONFIGS = [
  { key: 'sunday', label: '일요일', icon: '☀️' },
  { key: 'monday', label: '월요일', icon: '📅' },
  { key: 'tuesday', label: '화요일', icon: '📅' },
  { key: 'wednesday', label: '수요일', icon: '📅' },
  { key: 'thursday', label: '목요일', icon: '📅' },
  { key: 'friday', label: '금요일', icon: '📅' },
  { key: 'saturday', label: '토요일', icon: '☀️' },
];

const DEFAULT_OVERRIDE = {
  date: dayjs().format("YYYY-MM-DD"),
  is_open: true,
  start_time: "12:00",
  end_time: "18:00",
  lesson_restricted: true,
  lesson_restricted_times: [],
  reason: "",
  // 휴일 + 오픈 예외일에만 적용되는 평일 제한 항목 (기본: 제한 적용)
  weekday_same_day_only: true,
  weekday_time_restricted: true,
  weekday_booking_start_hour: 12,
  weekday_one_hour_limit: true,
  // 사용 불가능한 시간대 제한 적용 여부 (기본: 제한 적용, 해제 시 차단 시간 무시)
  apply_unavailable_times: true
};

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

function PracticeConfigDialog({ open, onClose, onSave }) {
  const { darkMode } = useDarkMode();
  const { confirm, toast } = useDialog();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [newOverride, setNewOverride] = useState({ ...DEFAULT_OVERRIDE });
  const [editingOverrideId, setEditingOverrideId] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);

  const ALL_ROOMS = ['Lesson 1', 'Lesson 2', 'Lesson 3', 'Lesson 4', 'Lesson 5', 'Lesson 6'];

  useEffect(() => {
    if (open) {
      fetchConfig();
      fetchOverrides();
      setEditingOverrideId(null);
    }
  }, [open]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await api.get('/practice-config');
      setConfig(res.data);
    } catch (err) {
      setError('설정을 불러오는데 실패했습니다.');
      setConfig({
        sunday_start_time: '12:00',
        sunday_end_time: '18:00',
        sunday_unavailable_times: [],
        sunday_unavailable_rooms: [],
        monday_start_time: '14:00',
        monday_end_time: '22:00',
        monday_unavailable_times: [],
        monday_unavailable_rooms: [],
        tuesday_start_time: '14:00',
        tuesday_end_time: '22:00',
        tuesday_unavailable_times: [],
        tuesday_unavailable_rooms: [],
        wednesday_start_time: '14:00',
        wednesday_end_time: '22:00',
        wednesday_unavailable_times: [],
        wednesday_unavailable_rooms: [],
        thursday_start_time: '14:00',
        thursday_end_time: '22:00',
        thursday_unavailable_times: [],
        thursday_unavailable_rooms: [],
        friday_start_time: '14:00',
        friday_end_time: '18:00',
        friday_unavailable_times: [],
        friday_unavailable_rooms: [],
        saturday_start_time: '12:00',
        saturday_end_time: '18:00',
        saturday_unavailable_times: [],
        saturday_unavailable_rooms: [],
        slot_duration: 30,
        lesson_restricted_rooms: ['Lesson 1', 'Lesson 2'],
        practice_rooms: ['Lesson 3', 'Lesson 4', 'Lesson 5', 'Lesson 6'],
        available_days: [0, 1, 2, 3, 4, 5, 6],
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrides = async () => {
    try {
      const res = await api.get('/practice-config/overrides');
      setOverrides(res.data);
    } catch (err) {
      console.error("예외 설정 로드 실패:", err);
      setOverrides([]);
    }
  };

  const generateTimeSlots = (start, end, duration = 30) => {
    const slots = [];
    if (!start || !end) return slots;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);

    let m = sh * 60 + sm;
    const endM = eh * 60 + em;

    while (m < endM) {
      const h = Math.floor(m / 60);
      const min = m % 60;
      slots.push(`${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`);
      m += duration;
    }
    return slots;
  };

  const toggleTime = (field, time) => {
    const list = config[field] || [];
    const updated = list.includes(time)
      ? list.filter(t => t !== time)
      : [...list, time];

    setConfig({ ...config, [field]: updated });
  };

  const toggleRoomByTime = (dayKey, time, room) => {
    const field = `${dayKey}_unavailable_rooms_by_time`;
    const roomsByTime = config[field] || {};
    const roomsForTime = roomsByTime[time] || [];

    const updatedRoomsForTime = roomsForTime.includes(room)
      ? roomsForTime.filter(r => r !== room)
      : [...roomsForTime, room];

    const updatedRoomsByTime = {
      ...roomsByTime,
      [time]: updatedRoomsForTime
    };

    // 빈 배열인 경우 해당 시간대 키를 삭제
    if (updatedRoomsForTime.length === 0) {
      delete updatedRoomsByTime[time];
    }

    setConfig({ ...config, [field]: updatedRoomsByTime });
  };

  const isRoomUnavailableAtTime = (dayKey, time, room) => {
    const field = `${dayKey}_unavailable_rooms_by_time`;
    const roomsByTime = config[field] || {};
    const roomsForTime = roomsByTime[time] || [];
    return roomsForTime.includes(room);
  };

  const handleSave = async (force = false) => {
    console.log('=== handleSave 시작 ===');
    console.log('force 파라미터:', force);
    console.log('현재 config 상태:', JSON.stringify(config, null, 2));
    console.log('saving 상태:', saving);

    try {
      console.log('setSaving(true) 호출');
      setSaving(true);
      setError(null);

      console.log('API PUT 요청 준비');
      console.log('요청 URL:', '/practice-config');
      console.log('요청 데이터:', { ...config, force });

      const response = await api.put('/practice-config', { ...config, force });

      console.log('API 응답 성공:', response);
      console.log('응답 status:', response.status);
      console.log('응답 data:', response.data);

      if (onSave && typeof onSave === 'function') {
        console.log('onSave() 콜백 호출');
        onSave();
      }

      if (onClose && typeof onClose === 'function') {
        console.log('onClose() 콜백 호출');
        onClose();
      }

      setConflicts(null);
      setConflictDialogOpen(false);
      console.log('=== handleSave 정상 완료 ===');
    } catch (err) {
      console.error('=== handleSave 에러 발생 ===');
      console.error('에러 객체:', err);
      console.error('에러 응답:', err?.response);
      console.error('에러 응답 status:', err?.response?.status);
      console.error('에러 응답 data:', err?.response?.data);

      if (err?.response?.status === 409 && err?.response?.data?.conflicts) {
        console.log('409 충돌 에러 - 충돌 다이얼로그 표시');
        console.log('충돌 목록:', err.response.data.conflicts);
        setConflicts(err.response.data.conflicts);
        setConflictDialogOpen(true);
        setError(null);
      } else {
        const errorMsg = err?.response?.data?.message || '설정 저장에 실패했습니다.';
        console.error('일반 에러 메시지 설정:', errorMsg);
        setError(errorMsg);
      }
    } finally {
      console.log('setSaving(false) 호출');
      setSaving(false);
      console.log('=== handleSave 종료 ===');
    }
  };

  const handleForceUpdate = () => {
    handleSave(true);
  };

  const handleEditOverrideClick = (o) => {
    setEditingOverrideId(o.id);
    setNewOverride({
      date: o.date,
      is_open: o.is_open,
      start_time: o.start_time ? o.start_time.slice(0, 5) : "12:00",
      end_time: o.end_time ? o.end_time.slice(0, 5) : "18:00",
      lesson_restricted: o.lesson_restricted ?? true,
      lesson_restricted_times: o.lesson_restricted_times || [],
      reason: o.reason || "",
      weekday_same_day_only: o.weekday_same_day_only ?? true,
      weekday_time_restricted: o.weekday_time_restricted ?? true,
      weekday_booking_start_hour: o.weekday_booking_start_hour ?? 12,
      weekday_one_hour_limit: o.weekday_one_hour_limit ?? true,
      apply_unavailable_times: o.apply_unavailable_times ?? true
    });
  };

  const handleCancelEdit = () => {
    setEditingOverrideId(null);
    setNewOverride({ ...DEFAULT_OVERRIDE, date: dayjs().format("YYYY-MM-DD") });
  };

  const handleAddOverride = async () => {
    if (!newOverride.date) {
      toast("날짜를 선택해주세요.", "warning");
      return;
    }

    const payload = {
      date: newOverride.date,
      is_open: newOverride.is_open,
      start_time: newOverride.is_open ? newOverride.start_time : null,
      end_time: newOverride.is_open ? newOverride.end_time : null,
      lesson_restricted: newOverride.lesson_restricted,
      lesson_restricted_times: newOverride.lesson_restricted && newOverride.lesson_restricted_times.length > 0
        ? newOverride.lesson_restricted_times
        : null,
      reason: newOverride.reason || null,
      weekday_same_day_only: newOverride.weekday_same_day_only,
      weekday_time_restricted: newOverride.weekday_time_restricted,
      weekday_booking_start_hour: newOverride.weekday_time_restricted ? newOverride.weekday_booking_start_hour : null,
      weekday_one_hour_limit: newOverride.weekday_one_hour_limit,
      apply_unavailable_times: newOverride.apply_unavailable_times
    };

    // 수정 모드: PUT (날짜 고정)
    if (editingOverrideId) {
      try {
        await api.put(`/practice-config/overrides/${editingOverrideId}`, payload);
        fetchOverrides();
        handleCancelEdit();
        toast("예외 설정이 수정되었습니다.");
        onSave?.();
      } catch (err) {
        toast(err?.response?.data?.message || "수정 실패", "error");
      }
      return;
    }

    // 추가 모드: POST
    try {
      await api.post("/practice-config/overrides", payload);

      fetchOverrides();
      setNewOverride({ ...DEFAULT_OVERRIDE, date: dayjs().format("YYYY-MM-DD") });
      toast("예외 설정이 추가되었습니다.");
      onSave?.();
    } catch (err) {
      toast(err?.response?.data?.message || "추가 실패", "error");
    }
  };

  const handleDeleteOverride = async (id) => {
    if (!(await confirm({ title: "예외 설정 삭제", message: "예외 설정을 삭제하시겠습니까?", confirmText: "삭제", color: "error" }))) return;

    try {
      await api.delete(`/practice-config/overrides/${id}`);
      fetchOverrides();
      onSave?.();
    } catch (err) {
      toast(err?.response?.data?.message || "삭제 실패", "error");
    }
  };

  if (loading || !config) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule color="primary" />
            <Typography variant="h6" fontWeight={600}>
              연습실 예약 설정
            </Typography>
          </Box>
        </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{ mb: 2 }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {DAY_CONFIGS.map((day, idx) => (
            <Tab
              key={day.key}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{day.icon}</span>
                  <span>{day.label}</span>
                </Box>
              }
            />
          ))}
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                ⚙️ 공통 설정
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventNote fontSize="small" /> 예외 날짜
              </Box>
            }
          />
        </Tabs>

        {/* 요일별 설정 탭 (0-6) */}
        {DAY_CONFIGS.map((day, idx) => (
          <TabPanel key={day.key} value={tabValue} index={idx}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography fontWeight={600} mb={2}>
                {day.label} 운영 시간
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>시작</InputLabel>
                  <Select
                    value={config[`${day.key}_start_time`]?.slice(0, 5) || '14:00'}
                    label="시작"
                    onChange={(e) =>
                      setConfig({ ...config, [`${day.key}_start_time`]: e.target.value })
                    }
                  >
                    {TIME_OPTIONS.map(time => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>종료</InputLabel>
                  <Select
                    value={config[`${day.key}_end_time`]?.slice(0, 5) || '22:00'}
                    label="종료"
                    onChange={(e) =>
                      setConfig({ ...config, [`${day.key}_end_time`]: e.target.value })
                    }
                  >
                    {TIME_OPTIONS.map(time => (
                      <MenuItem key={time} value={time}>{time}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <Typography fontWeight={600} mb={1}>
                사용 불가능한 시간대 (모든 연습실 예약 차단)
              </Typography>
              <Typography variant="caption" color="text.secondary" mb={1}>
                선택한 시간대는 모든 연습실의 예약이 차단됩니다.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {generateTimeSlots(
                  config[`${day.key}_start_time`],
                  config[`${day.key}_end_time`]
                ).map(time => {
                  const unavailableTimes = config[`${day.key}_unavailable_times`] || [];
                  const isUnavailable = unavailableTimes.includes(time);
                  return (
                    <Chip
                      key={time}
                      label={time}
                      clickable
                      color={isUnavailable ? 'error' : 'default'}
                      onClick={() => toggleTime(`${day.key}_unavailable_times`, time)}
                    />
                  );
                })}
              </Box>
              {(config[`${day.key}_unavailable_times`] || []).length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  선택된 사용 불가 시간: {(config[`${day.key}_unavailable_times`] || []).sort().join(', ')}
                </Typography>
              )}
            </FormControl>

            <FormControl fullWidth>
              <Typography fontWeight={600} mb={1} color="error">
                시간대별 사용 불가능한 연습실 설정
              </Typography>
              <Typography variant="caption" color="text.secondary" mb={1}>
                각 시간대별로 사용 불가능한 연습실을 선택하세요. 선택된 연습실은 해당 시간에 예약할 수 없습니다.
              </Typography>
              <Paper sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: darkMode ? 'grey.800' : '#f5f5f5', minWidth: 70 }}>시간</TableCell>
                      {ALL_ROOMS.map(room => (
                        <TableCell key={room} align="center" sx={{ fontWeight: 600, bgcolor: darkMode ? 'grey.800' : '#f5f5f5', minWidth: 80, fontSize: '0.75rem' }}>
                          {room}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {generateTimeSlots(
                      config[`${day.key}_start_time`],
                      config[`${day.key}_end_time`]
                    ).map(time => {
                      const isTimeUnavailable = (config[`${day.key}_unavailable_times`] || []).includes(time);
                      return (
                        <TableRow key={time} sx={{ bgcolor: isTimeUnavailable ? (darkMode ? 'rgba(255, 235, 238, 0.2)' : '#ffebee') : 'inherit' }}>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {time}
                            {isTimeUnavailable && (
                              <Typography variant="caption" color="error" display="block" fontSize="0.6rem">
                                전체 차단
                              </Typography>
                            )}
                          </TableCell>
                          {ALL_ROOMS.map(room => {
                            const isUnavailable = isRoomUnavailableAtTime(day.key, time, room);
                            return (
                              <TableCell key={room} align="center" sx={{ p: 0.5 }}>
                                <Chip
                                  size="small"
                                  label={isUnavailable ? '불가' : '가능'}
                                  clickable
                                  disabled={isTimeUnavailable}
                                  color={isUnavailable ? 'error' : 'default'}
                                  variant={isUnavailable ? 'filled' : 'outlined'}
                                  onClick={() => !isTimeUnavailable && toggleRoomByTime(day.key, time, room)}
                                  sx={{
                                    minWidth: 50,
                                    fontSize: '0.7rem',
                                    opacity: isTimeUnavailable ? 0.5 : 1
                                  }}
                                />
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
              {Object.keys(config[`${day.key}_unavailable_rooms_by_time`] || {}).length > 0 && (
                <Typography variant="caption" color="error.main" sx={{ mt: 1, fontWeight: 600 }}>
                  시간대별 사용 불가 설정: {Object.keys(config[`${day.key}_unavailable_rooms_by_time`] || {}).length}개 시간대
                </Typography>
              )}
            </FormControl>

            <Button
              variant="contained"
              onClick={(e) => {
                console.log('저장 버튼 클릭됨! 이벤트:', e);
                console.log('현재 day:', day.key);
                handleSave();
              }}
              disabled={saving}
              sx={{ mt: 3 }}
            >
                {saving ? '저장 중...' : `${day.label} 설정 저장`}
            </Button>
          </TabPanel>
        ))}

        {/* 공통 설정 탭 */}
        <TabPanel value={tabValue} index={7}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography fontWeight={600} mb={2}>
              평일 당일 예약 오픈
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              평일 연습실은 먼 날짜 선점을 막기 위해 당일에만 예약할 수 있으며, 매일 이 시각에 예약이 열립니다.
            </Typography>
            <FormControl fullWidth size="small" sx={{ maxWidth: 200 }}>
              <InputLabel>오픈 시각</InputLabel>
              <Select
                value={config.weekday_booking_start_hour ?? 12}
                label="오픈 시각"
                onChange={(e) =>
                  setConfig({ ...config, weekday_booking_start_hour: e.target.value })
                }
              >
                {[...Array(24)].map((_, hour) => (
                  <MenuItem key={hour} value={hour}>{hour}시</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              예: 12시로 설정하면 평일 당일 12시에 예약이 열립니다
            </Typography>
          </Paper>

          <Button
            variant="contained"
            onClick={() => handleSave()}
            disabled={saving}
            sx={{ mt: 2 }}
          >
            {saving ? '저장 중...' : '공통 설정 저장'}
          </Button>
        </TabPanel>

        {/* 예외 날짜 설정 탭 */}
        <TabPanel value={tabValue} index={8}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            특정 날짜에 기본 설정과 다른 운영 시간을 적용하거나 휴무일로 지정할 수 있습니다.
          </Typography>

          <Paper sx={{ p: 2, mb: 3, bgcolor: darkMode ? 'grey.800' : '#f5f5f5' }}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
              <DateField
                label="날짜"
                size="small"
                value={newOverride.date}
                onChange={(date) => setNewOverride({ ...newOverride, date })}
                disabled={!!editingOverrideId}
                sx={{ width: 170 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>유형</InputLabel>
                <Select
                  value={newOverride.is_open ? 'open' : 'closed'}
                  label="유형"
                  onChange={(e) => setNewOverride({
                    ...newOverride,
                    is_open: e.target.value === 'open'
                  })}
                >
                  <MenuItem value="open">운영 시간 변경</MenuItem>
                  <MenuItem value="closed">휴무</MenuItem>
                </Select>
              </FormControl>
              {newOverride.is_open && (
                <>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>시작</InputLabel>
                    <Select
                      value={newOverride.start_time}
                      label="시작"
                      onChange={(e) => setNewOverride({ ...newOverride, start_time: e.target.value })}
                    >
                      {TIME_OPTIONS.map(time => (
                        <MenuItem key={time} value={time}>{time}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2">~</Typography>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>종료</InputLabel>
                    <Select
                      value={newOverride.end_time}
                      label="종료"
                      onChange={(e) => setNewOverride({ ...newOverride, end_time: e.target.value })}
                    >
                      {TIME_OPTIONS.map(time => (
                        <MenuItem key={time} value={time}>{time}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
            </Box>
            {newOverride.is_open && (
              <Box sx={{ mb: 1 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={newOverride.apply_unavailable_times}
                    onChange={(e) => setNewOverride({ ...newOverride, apply_unavailable_times: e.target.checked })} />}
                  label="사용 불가능한 시간대 제한 적용"
                />
                {!newOverride.apply_unavailable_times && (
                  <Typography variant="caption" color="warning.main" sx={{ display: "block", ml: 4 }}>
                    이 날짜는 요일별 '사용 불가능한 시간대' 차단을 무시하고 전체 시간 예약을 허용합니다. (개별 연습실 차단은 유지)
                  </Typography>
                )}
              </Box>
            )}
            {newOverride.is_open && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 1, mb: 1, border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={newOverride.weekday_same_day_only}
                    onChange={(e) => setNewOverride({ ...newOverride, weekday_same_day_only: e.target.checked })} />}
                  label="당일만 예약 가능 제한"
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={newOverride.weekday_time_restricted}
                      onChange={(e) => setNewOverride({ ...newOverride, weekday_time_restricted: e.target.checked })} />}
                    label="당일 예약 오픈 제한"
                  />
                  {newOverride.weekday_time_restricted && (
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>오픈 시각</InputLabel>
                      <Select
                        value={newOverride.weekday_booking_start_hour}
                        label="오픈 시각"
                        onChange={(e) => setNewOverride({ ...newOverride, weekday_booking_start_hour: e.target.value })}
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <MenuItem key={h} value={h}>{h}시</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
                <FormControlLabel
                  control={<Checkbox size="small" checked={newOverride.weekday_one_hour_limit}
                    onChange={(e) => setNewOverride({ ...newOverride, weekday_one_hour_limit: e.target.checked })} />}
                  label="최대 1시간(30분 2회) 이용 제한"
                />
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                label="사유"
                size="small"
                value={newOverride.reason}
                onChange={(e) => setNewOverride({ ...newOverride, reason: e.target.value })}
                sx={{ minWidth: 200, flex: 1 }}
                placeholder={newOverride.is_open ? "예: 특별 운영" : "예: 명절 휴무"}
              />
              {editingOverrideId ? (
                <>
                  <Tooltip title="수정 저장">
                    <IconButton color="primary" onClick={handleAddOverride} aria-label="수정 저장">
                      <Check />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="취소">
                    <IconButton onClick={handleCancelEdit} aria-label="취소">
                      <Close />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <IconButton color="primary" onClick={handleAddOverride}>
                  <Add />
                </IconButton>
              )}
            </Box>
          </Paper>

          {newOverride.date && (
            <Box sx={{ mb: 3, mt: -2 }}>
              <DateImpactPreview
                date={newOverride.date}
                change={{
                  kind: "PRACTICE_OVERRIDE",
                  payload: {
                    is_open: newOverride.is_open,
                    start_time: newOverride.is_open ? newOverride.start_time : null,
                    end_time: newOverride.is_open ? newOverride.end_time : null,
                    apply_unavailable_times: newOverride.apply_unavailable_times
                  }
                }}
              />
            </Box>
          )}

          {overrides.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
              등록된 예외 설정이 없습니다.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>날짜</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell>운영 시간</TableCell>
                  <TableCell>사유</TableCell>
                  <TableCell width={50}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {overrides.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell>
                      {o.date}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({["일","월","화","수","목","금","토"][new Date(o.date).getDay()]})
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={o.is_open ? "운영" : "휴무"}
                        size="small"
                        color={o.is_open ? "success" : "error"}
                        variant="outlined"
                      />
                      {o.is_open && o.apply_unavailable_times === false && (
                        <Chip
                          label="차단시간 해제"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {o.is_open
                        ? `${o.start_time?.slice(0, 5) || "-"} ~ ${o.end_time?.slice(0, 5) || "-"}`
                        : "-"
                      }
                    </TableCell>
                    <TableCell>{o.reason || "-"}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="primary" onClick={() => handleEditOverrideClick(o)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteOverride(o.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TabPanel>
      </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={saving}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>

      {/* 충돌 예약 확인 다이얼로그 */}
      <Dialog open={conflictDialogOpen} onClose={() => setConflictDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            <Typography variant="h6" fontWeight={600}>
              예약 충돌 경고
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            변경하려는 설정과 충돌하는 예약이 <strong>{conflicts?.length || 0}건</strong> 있습니다.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            아래 예약들은 새로운 설정에서 운영 시간 외이거나 사용 불가 시간에 해당합니다.
            계속 진행하시면 이 예약들은 그대로 유지되지만, 회원들이 혼란을 겪을 수 있습니다.
          </Typography>
          <Paper sx={{ maxHeight: 300, overflow: 'auto', p: 2 }}>
            <List dense>
              {conflicts?.map((conflict, index) => (
                <ListItem
                  key={conflict.id}
                  sx={{
                    mb: 1,
                    border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #e0e0e0',
                    borderRadius: 1,
                    bgcolor: darkMode ? 'grey.900' : '#fafafa'
                  }}
                >
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {conflict.user_name} ({conflict.room_name})
                        </Typography>
                        <Chip
                          label={conflict.reason}
                          size="small"
                          color="error"
                          sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {conflict.date} {conflict.start_time.slice(0, 5)} ~ {conflict.end_time.slice(0, 5)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 600 }}>
            권장: 해당 예약들을 먼저 취소하거나 회원들에게 연락한 후 설정을 변경하세요.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConflictDialogOpen(false)} variant="outlined">
            취소
          </Button>
          <Button onClick={handleForceUpdate} variant="contained" color="warning" disabled={saving}>
            {saving ? '저장 중...' : '무시하고 저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PracticeConfigDialog;
